import { createRouter, createWebHistory, type RouteLocationRaw, type Router, type RouterHistory } from "vue-router";
import type { QueryClient } from "@tanstack/vue-query";
import { UnauthenticatedError } from "../api/client.ts";
import { BRAND } from "../lib/brand.ts";
import { safeReturnPath } from "../lib/return-path.ts";
import { decideAccess, homeFor, type Session } from "../session/access.ts";
import { forgetSession, lastKnownSession, SESSION_KEY, sessionQueryOptions } from "../session/session.ts";
import { settingsFor } from "../session/settings.ts";
import { portalRoutes } from "./routes.ts";

/**
 * Query flag of `/login` after a lost session: the login dialog then says that the session has
 * ended (AP-F5, TL 2026-10-08). The portal sets and reads it; the API never sees it. Not
 * `expired`, which is the code of `/login?error=` for a login state that timed out (08 §1).
 */
export const SESSION_ENDED_FLAG = "ended";

/** The login with the way back to `path`, flagged when a session ended on the way. */
function loginFor(path: string, ended: boolean): RouteLocationRaw {
  return { name: "login", query: { next: path, ...(ended ? { [SESSION_ENDED_FLAG]: "1" } : {}) } };
}

/**
 * Routers whose guard is fetching the session right now. A 401 seen there reaches
 * `handleSessionLost` through the query client while the guard still waits; the guard then
 * leads to the login itself, to the page the user asked for.
 */
const checkingSession = new WeakSet<Router>();

/**
 * The session for the guard: from the cache while fresh, else from the API. `ended`: the cache
 * held a session and the API now answers 401 (`fetchSession` raises it only then), so it ended
 * while the user was working. Any other failure (5xx, a `429` of the rate limit, the network, an
 * answer outside the contract, which `fetchSession` has reported) says nothing about the session:
 * the guard goes on with the last one the cache knew, so nobody signed in is sent to the login
 * (GP1, GI1); without one, as a guest.
 */
async function sessionFor(router: Router, queryClient: QueryClient): Promise<{ session: Session; ended: boolean }> {
  checkingSession.add(router);
  try {
    return { session: await queryClient.query(sessionQueryOptions()), ended: false };
  } catch (error) {
    if (error instanceof UnauthenticatedError) return { session: null, ended: true };
    return { session: lastKnownSession(queryClient), ended: false };
  } finally {
    checkingSession.delete(router);
  }
}

export type PortalRouterOptions = {
  queryClient: QueryClient;
  history?: RouterHistory;
};

/**
 * The router with the one guard of ADR-066: guests go to the login with the return path,
 * routes the session does not allow show "not found" (K-05), `/` leads to the actor's home. A
 * session that ended since the last navigation is forgotten and leads to the login with the
 * note, whatever the target, as a 401 of any read does (`handleSessionLost`). Next to the session
 * the guard reads the portal's settings, once per load: with the guest view off, the catalogue
 * and the product pages need a session too (ADR-133 point 3); while they cannot be read, the
 * defaults, and the next navigation asks again (GP4).
 */
export function createPortalRouter({ queryClient, history = createWebHistory() }: PortalRouterOptions): Router {
  const router = createRouter({ history, routes: portalRoutes });

  router.beforeEach(async (to): Promise<true | RouteLocationRaw> => {
    const [{ session, ended }, settings] = await Promise.all([
      sessionFor(router, queryClient),
      settingsFor(queryClient),
    ]);
    if (ended) {
      forgetSession(queryClient);
      return to.name === "login" ? true : loginFor(to.fullPath, true);
    }
    if (to.name === "login" && session) return safeReturnPath(to.query.next) ?? { name: "home" };
    if (to.name === "home") {
      const home = homeFor(session, settings);
      if (home !== "home") return { name: home };
    }
    const decision = decideAccess(to.meta.access, session, settings);
    if (decision === "login") return loginFor(to.fullPath, false);
    if (decision === "not_found") {
      return { name: "notFound", params: { pathMatch: to.path.slice(1).split("/") }, query: to.query, hash: to.hash };
    }
    return true;
  });

  // a cancelled or refused navigation leaves the page on screen, and with it its title (PT7)
  router.afterEach((to, _from, failure) => {
    if (!failure) document.title = `${to.meta.title} · ${BRAND}`;
  });

  return router;
}

/**
 * The session ended under the user's feet (any 401, ADR-076 point 5): forget every server
 * datum, mark "no session", and open the login with the way back to where the user was and the
 * flag that tells the dialog why. A guest sent to the login by the guard gets no flag. While the
 * guard is checking the session it leads to the login itself.
 */
export function handleSessionLost(router: Router, queryClient: QueryClient): void {
  // Already a guest: nothing was lost. A 401 of a query that is still mounted while the logout
  // navigates away, or of a second read after the first 401, must not open the login again
  // (ADR-083 point 2 and 5).
  if (queryClient.getQueryData(SESSION_KEY) === null) return;
  forgetSession(queryClient);
  if (checkingSession.has(router)) return;
  const current = router.currentRoute.value;
  if (current.name === "login") return;
  void router.push(loginFor(current.fullPath, true));
}
